import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2, Repeat2 } from "lucide-react";
import { api, formatErr } from "../../lib/api";

/**
 * ThreadConvertCard (iter157) — one-tap admin action to move a community
 * forum thread into the Trip Wall. Lists 20 most-recent threads with a
 * "Convert to trip share" button next to each.
 */
export default function ThreadConvertCard() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/threads/recent");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const convert = async (threadId) => {
    if (!window.confirm("Move this community thread into the Trip Wall? The thread will be deleted from the forum.")) return;
    setBusy(threadId);
    try {
      const { data } = await api.post(`/admin/threads/${threadId}/convert-to-trip-story`);
      toast.success(`Converted to trip share for ${data.campsite}.`);
      setItems((prev) => prev.filter((t) => t.thread_id !== threadId));
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(null); }
  };

  return (
    <section
      className="border hairline rounded-2xl p-5 sm:p-6 mb-6 bg-white"
      data-testid="admin-thread-convert"
    >
      <div className="min-w-0 mb-4">
        <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-1">
          <Repeat2 className="w-3 h-3" /> Thread → Trip Share
        </div>
        <h2 className="font-display text-xl sm:text-2xl tracking-tight">
          Move a community post to the Trip Wall
        </h2>
        <p className="text-xs text-[#666] mt-1 max-w-xl">
          When a camper posts a full review as a forum thread by mistake, tap
          Convert here and it becomes a proper trip share (attributed to them,
          pinned to the matching campsite). The original thread is deleted.
        </p>
      </div>

      {loading ? (
        <div className="text-xs text-[#888]">Loading recent threads…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-[#888]">No recent threads.</div>
      ) : (
        <ul className="divide-y hairline">
          {items.map((t) => (
            <li key={t.thread_id} className="py-3 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{t.title || "(untitled)"}</div>
                <div className="text-xs text-[#666] line-clamp-2 mt-0.5">
                  {(t.body || t.content || "").slice(0, 140)}
                </div>
                <div className="text-[10px] text-[#999] mt-1">
                  {t.author_name || "Anonymous"} · {t.created_at ? new Date(t.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}
                </div>
              </div>
              <button
                type="button"
                disabled={busy === t.thread_id}
                onClick={() => convert(t.thread_id)}
                className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#ff4500] text-white text-[10px] font-black uppercase tracking-wider hover:bg-[#e63e00] disabled:opacity-60"
                data-testid={`thread-convert-${t.thread_id}`}
              >
                {busy === t.thread_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                Convert
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
